/*
 * The analytics screens (night-run AN-07, CMP-06): address, name, summary and who may open each one. The index page
 * lists them from here, and SEAM-03 (App.tsx routes, Layout.tsx menu) can read the same list instead of repeating
 * paths and permissions. The permissions mirror the server checks of /v1/analytics.
 */

export type AnalyticsScreenId =
  'anomalias' | 'previsao' | 'simulador' | 'assistente' | 'calendario' | 'comparativos';

export interface AnalyticsScreen {
  id: AnalyticsScreenId;
  /** Route under the app root. */
  path: string;
  /** Named export of the page module (lazy-screen.tsx `screen(loader, name)`). */
  component: string;
  title: string;
  summary: string;
  /** The person needs at least one of these in the selected unit. */
  anyOf: readonly string[];
}

export const ANALYTICS_INDEX_PATH = '/analises';

export const ANALYTICS_SCREENS: readonly AnalyticsScreen[] = [
  {
    id: 'anomalias',
    path: '/analises/anomalias',
    component: 'AnomaliesPage',
    title: 'Anomalias diárias',
    summary:
      'Dias aprovados com receita, acessos ou pagantes fora do padrão do mesmo dia da semana, com a explicação do cálculo.',
    anyOf: ['report.read'],
  },
  {
    id: 'previsao',
    path: '/analises/previsao',
    component: 'ForecastStatusPage',
    title: 'Previsão de receita',
    summary:
      'Situação do histórico e dos critérios de publicação. A previsão não é exibida enquanto os critérios não forem aceitos.',
    anyOf: ['report.read'],
  },
  {
    id: 'simulador',
    path: '/analises/simulador',
    component: 'ScenarioPage',
    title: 'Simulador de cenários',
    summary:
      'Recalcula um período já ocorrido com variações de ticket médio e de volume informadas por você. Nada é gravado.',
    anyOf: ['report.read'],
  },
  {
    id: 'assistente',
    path: '/analises/assistente',
    component: 'AssistantPage',
    title: 'Assistente de consultas',
    summary:
      'Perguntas em português sobre receita, acessos, pendências e conciliação, respondidas por consultas autorizadas.',
    anyOf: ['dashboard.read', 'finance.read', 'exception.read', 'alert.read'],
  },
  {
    id: 'calendario',
    path: '/analises/calendario',
    component: 'CalendarPage',
    title: 'Calendário operacional',
    summary: 'Feriados, dias fechados e eventos que mudam o tipo do dia nos comparativos ajustados.',
    anyOf: ['report.read', 'rules.manage'],
  },
  {
    id: 'comparativos',
    path: '/analises/comparativos',
    component: 'ComparativesAdjustedPage',
    title: 'Comparativos ajustados',
    summary:
      'Período atual contra a base, com dias faltantes, ajuste pela composição de tipos de dia e efeitos de preço e volume.',
    anyOf: ['report.read'],
  },
];

export const canOpen = (screen: AnalyticsScreen, can: (permission: string) => boolean) =>
  screen.anyOf.some((p) => can(p));
